<section class="preservation-messages">
  <app-page-header
    title="Mensajes de preservación digital"
    subtitle="Comparte mensajes educativos con los usuarios en su inicio."
    icon="MessageSquareDot"
  />

  @if (!canManage()) {
    <div class="preservation-messages__denied card-like">
      <p class="preservation-messages__denied-title">Mensajes informativos</p>
      <p class="preservation-messages__denied-text">
        No tienes permisos para administrar esta sección. Si necesitas publicar mensajes, contacta a un
        administrador.
      </p>
    </div>
  } @else {
    @if (error()) {
      <app-vault-page-alert
        type="error"
        title="Acción incompleta"
        [message]="error()"
        (closed)="error.set(null)"
      />
    }

    <div class="preservation-messages__toolbar">
      <lib-button variant="primary" type="button" prefixIcon="Plus" [disabled]="loading()" (clicked)="openCreate()">
        Nuevo mensaje
      </lib-button>
      <lib-button variant="secondary" type="button" prefixIcon="RefreshCcw" [loading]="loading()" (clicked)="reload()">
        Actualizar
      </lib-button>
    </div>

    @if (loading()) {
      <div class="preservation-messages__skeleton" aria-busy="true">
        <lib-skeleton variant="rectangle" [height]="'160px'" />
        <lib-skeleton variant="rectangle" [height]="'160px'" />
      </div>
    } @else if (items().length === 0) {
      <lib-empty
        icon="MessageSquareDot"
        message="No hay mensajes publicados"
        description="Crea el primero para mostrarlo en el inicio."
      />
      <div class="preservation-messages__empty-cta">
        <lib-button variant="primary" type="button" (clicked)="openCreate()">Crear primer mensaje</lib-button>
      </div>
    } @else {
      <div class="preservation-messages__grid">
        @for (msg of items(); track msg.id) {
          <article
            class="preservation-messages__card"
            [class.preservation-messages__card--inactive]="!msg.is_active"
          >
            <header class="preservation-messages__card-head">
              <div>
                <h2 class="preservation-messages__card-title">{{ msg.title }}</h2>
                <div class="preservation-messages__badges">
                  <lib-badge [text]="audienceBadgeLabel(msg)" variant="neutral" size="small" />
                  <lib-badge
                    [text]="msg.is_active ? 'Activo' : 'Inactivo'"
                    [variant]="msg.is_active ? 'success' : 'neutral'"
                    size="small"
                  />
                </div>
                <p class="preservation-messages__card-meta">Publicado el {{ formatWhen(msg.created_at) }}</p>
              </div>
              <lib-checkbox
                [label]="'Activo'"
                [checked]="msg.is_active"
                [disabled]="busyId() === msg.id"
                (checkedChange)="toggleActive(msg, $event)"
              />
            </header>
            <p class="preservation-messages__card-body">{{ msg.message }}</p>
            @if (msg.starts_at || msg.ends_at) {
              <div class="preservation-messages__card-dates">
                @if (msg.starts_at) {
                  <span>Inicio: {{ formatWhen(msg.starts_at) }}</span>
                }
                @if (msg.ends_at) {
                  <span>Fin: {{ formatWhen(msg.ends_at) }}</span>
                }
              </div>
            }
            <footer class="preservation-messages__card-actions">
              <lib-button variant="secondary" type="button" (clicked)="openEdit(msg)">
                Editar
              </lib-button>
              <lib-button
                variant="secondary"
                type="button"
                class="preservation-messages__btn-danger"
                (clicked)="deleteMessage(msg)"
              >
                Eliminar
              </lib-button>
            </footer>
          </article>
        }
      </div>
    }
  }
</section>

@if (dialogOpen()) {
  <div
    class="preservation-messages__backdrop"
    (click)="closeDialog()"
    (keydown.escape)="closeDialog()"
    tabindex="-1"
    role="presentation"
  ></div>
  <div class="preservation-messages__dialog" role="dialog" aria-modal="true" aria-labelledby="pm-dialog-title">
    <header class="preservation-messages__dialog-head">
      <h2 id="pm-dialog-title">{{ editing() ? 'Editar mensaje' : 'Nuevo mensaje' }}</h2>
      <p class="preservation-messages__dialog-desc">
        Define el mensaje que verán los usuarios en su inicio.
      </p>
    </header>

    <div class="preservation-messages__dialog-body" (click)="$event.stopPropagation()">
      <app-grid-form preset="2cols" [columns]="2" gap="1rem" wrapperClass="preservation-messages__form">
        <app-grid-form-item [span]="'full'" class="preservation-messages__field">
          <app-field-label label="Título" [required]="true" />
          <lib-input
            label=""
            placeholder="Título breve"
            [value]="formTitle()"
            (valueChange)="formTitle.set($event)"
          />
        </app-grid-form-item>
        <app-grid-form-item [span]="'full'" class="preservation-messages__field preservation-messages__field--message">
          <app-field-label label="Mensaje" [required]="true" />
          <lib-textarea
            label=""
            [rows]="5"
            placeholder="Texto educativo sobre preservación y el sistema"
            [value]="formMessage()"
            (valueChange)="formMessage.set($event)"
          />
        </app-grid-form-item>
        <app-grid-form-item [span]="showAudienceTarget() ? 1 : 'full'" class="preservation-messages__field">
          <app-field-label label="Alcance" />
          <lib-select
            label=""
            [options]="audienceOptions"
            [value]="formAudienceScope()"
            (valueChange)="onAudienceChange($event)"
          />
        </app-grid-form-item>
        @if (showAudienceRole()) {
          <app-grid-form-item [span]="1" class="preservation-messages__field">
            <app-field-label label="Rol destino" />
            <lib-select
              label=""
              placeholder="Selecciona un rol"
              [options]="audienceRoleOptions"
              [value]="formAudienceRole()"
              (valueChange)="onAudienceRoleChange($event)"
            />
          </app-grid-form-item>
        }
        @if (showAudienceTenant()) {
          <app-grid-form-item [span]="1" class="preservation-messages__field">
            <app-field-label label="Empresa destino" />
            <lib-select
              label=""
              placeholder="Selecciona una empresa"
              [options]="tenantOptions()"
              [value]="formAudienceTenantId()"
              (valueChange)="onAudienceTenantChange($event)"
            />
          </app-grid-form-item>
        }
        <app-grid-form-item [span]="1" class="preservation-messages__field">
          <app-field-label label="Fecha inicio" />
          <input
            type="datetime-local"
            class="preservation-messages__datetime"
            [value]="formStartsAt()"
            (input)="onStartsAtInput($event)"
          />
        </app-grid-form-item>
        <app-grid-form-item [span]="1" class="preservation-messages__field">
          <app-field-label label="Fecha fin" />
          <input
            type="datetime-local"
            class="preservation-messages__datetime"
            [value]="formEndsAt()"
            (input)="onEndsAtInput($event)"
          />
        </app-grid-form-item>
        <app-grid-form-item [span]="'full'" class="preservation-messages__field preservation-messages__field--active">
          <lib-checkbox
            label="Mensaje activo"
            [checked]="formActive()"
            (checkedChange)="formActive.set($event)"
          />
        </app-grid-form-item>
      </app-grid-form>

      <div class="preservation-messages__suggested">
        <p class="preservation-messages__suggested-label">Usar mensaje sugerido</p>
        <div class="preservation-messages__suggested-btns">
          @for (s of defaultSuggested; track s.title) {
            <lib-button variant="secondary" type="button" (clicked)="applySuggested(s)">
              {{ s.title }}
            </lib-button>
          }
        </div>
      </div>

      <footer class="preservation-messages__dialog-footer">
        <lib-button variant="secondary" type="button" [disabled]="saving()" (clicked)="closeDialog()">
          Cancelar
        </lib-button>
        <lib-button variant="primary" type="button" [loading]="saving()" (clicked)="save()">
          {{ editing() ? 'Guardar cambios' : 'Crear mensaje' }}
        </lib-button>
      </footer>
    </div>
  </div>
}
